import { fetchEventSource } from '@microsoft/fetch-event-source'
import { Message } from './store'

interface ErrorDetail {
  status_code: number
  description: string
}

class ApiError extends Error {
  details: ErrorDetail

  constructor(details: ErrorDetail) {
    super(`${details.status_code}: ${details.description}`)
    this.details = details
  }
}
// Default BASE_URL is now managed in the store

const API_PATHS = {
  searchMultiAgents: '/v1/butler/travel_multi_agent/search',
  updateUserFeedback: '/v1/butler/travel_multi_agent/feedback',
}

export interface ProtoMessage {
  role: 'ROLE_UNSPECIFIED' | 'HUMAN' | 'AI'
  content: string
}

export interface SearchRequest {
  sessionId?: string
  query?: string
  history?: ProtoMessage[]
  useMockData?: boolean
  afterChatId?: string
}

export interface DateRange {
  startDate: string
  endDate: string
}

export interface FlexibleFlightSearch {
  dateRange: DateRange
  tripDuration: number
}

export interface JourneyInfo {
  departureAirport: string
  arrivalAirport: string
  departureDate: string
  departureHourRange: number[]
  arrivalHourRange: number[]
}

export interface PriceRange {
  minPrice: number
  maxPrice: number
}

export interface FlightParameters {
  flexibleSearch: FlexibleFlightSearch
  journeyInfos: JourneyInfo[]
  adultCount?: number
  childCount?: number
  infantCount?: number
  cabinClass: string[]
  airlines: string[]
  numberOfConnections?: number
  maxLayoverMinutes?: number
  flightDurationMinutes?: number
  numberOfFreeBaggage?: number
  refundability: string
  changeability: string
  freeMealAvailability: string
  priceRange: PriceRange
}

export interface Journey {
  airline: string
  airlineName: string
  arrAirport: string
  arrRegionCode: string
  arrivalDate: string
  departureDate: string
  deptAirport: string
  deptRegionCode: string
  journeyKey: string
  journeyType: string
  pairKey: string
  arrAirportName: string
  deptAirportName: string
  segments: Segment[]
  fares: FlightFare
  freessrs: Freessrs[]
}

export interface Freessrs {
  ssrType: string
  ssrName: string
  ssrUnit: string
  ssrService: {
    paxType: string
    quantity: number
    ssrValue: string
    ssrCode: string
  }[]
}

export interface FlightFare {
  totalFare: number
  totalPromotionFare: number
}

export interface Segment {
  deptAirport: string
  arrAirport: string
  departureDate: string
  arrivalDate: string
  carrierCode: string
  flightNumber: string
  flightTime: number
  waitingTime?: number
  bookingClass: string
  fareBasisCode: string
  cabinClass: string
}

export interface FlightResult {
  id: string
  journeys: Journey[]
}

export interface FlightGroupResult {
  departureDate: string
  flights: FlightResult[]
}

export interface FlightSearchResults {
  flights: FlightResult[]
  isFlexibleSearch: boolean
  groupedFlights: FlightGroupResult[]
}

export interface FlexibleHotelSearch {
  dateRange: DateRange
  stayDuration: number
}

export interface Occupancy {
  adultCount: number
  childCount: number
}

export interface BedType {
  type: string
  count: number
}

export interface HotelParameters {
  flexibleSearch: FlexibleHotelSearch
  checkInDate: string
  checkOutDate: string
  location: string
  occupancyPerRoom: Occupancy[]
  roomGrade: string
  bedTypes: BedType[]
  view: string
  starRatings: number[]
  facilities: string[]
  amenities: string[]
  priceRange: PriceRange
  currency: string
  accommodationTypes: string[]
  breakfastIncluded: boolean
}

export interface HotelResult {
  id: string
  name: string
  imageUrl: string
  price: string
  checkInDate: string
  checkOutDate: string
  landingUrl: string
  // Additional hotel details would be added here as they are implemented in the proto
}

export interface HotelSearchResults {
  hotels: HotelResult[]
  // Additional metadata would be added here as they are implemented in the proto
}

export interface SearchResponse {
  sessionId: string
  userQueryId: string
  responseId: string
  traceIds: string[]
  textResponse: string
  history: ProtoMessage[]
  flightParams?: FlightParameters
  flightResults?: FlightSearchResults
  hotelParams?: HotelParameters
  hotelResults?: HotelSearchResults
}

export interface AgentStatus {
  currentAgent:
    | 'UNKNOWN_AGENT'
    | 'PLANNER'
    | 'FLIGHT_EXACT'
    | 'FLIGHT_FLEXIBLE'
    | 'HOTEL_EXACT'
    | 'HOTEL_FLEXIBLE'
    | 'CHAT_RESPONSE'
  state: 'PROCESSING' | 'API_CALL' | 'COMPLETED' | 'ERROR'
  message: string
  progressPercentage: number
}

export interface SearchStreamResponse {
  status: AgentStatus
  finalResponse?: SearchResponse
}

export const appMessageToProtoMessage = (message: Message): ProtoMessage => {
  return {
    role: message.sender === 'user' ? 'HUMAN' : 'AI',
    content: message.text,
  }
}

export const searchMultiAgents = async (
  baseUrl: string,
  query: string,
  history: Message[],
  sessionId?: string,
  useMockData?: boolean,
  afterChatId?: string,
  onMessage?: (response: SearchStreamResponse) => void,
  signal?: AbortSignal,
): Promise<SearchResponse> => {
  const url = `${baseUrl}${API_PATHS.searchMultiAgents}`
  const protoHistory = history.map(appMessageToProtoMessage)

  const payload: SearchRequest = {
    sessionId,
    query,
    history: protoHistory,
    useMockData,
    afterChatId,
  }

  let finalResponse: SearchResponse | undefined

  return new Promise((resolve, reject) => {
    void fetchEventSource(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal,
      onmessage(ev) {
        try {
          if (ev.event === 'error') {
            throw new ApiError(JSON.parse(ev.data))
          }

          if (ev.event === 'message') {
            const streamResponse = JSON.parse(ev.data) as SearchStreamResponse
            if (onMessage) {
              onMessage(streamResponse)
            }

            // Check if this is the final response
            if (streamResponse.status.state === 'COMPLETED' && streamResponse.finalResponse) {
              finalResponse = streamResponse.finalResponse
            }
          }
        } catch (error) {
          console.error('Error parsing stream response:', error)
        }
      },
      onclose() {
        if (finalResponse) {
          resolve(finalResponse)
        } else {
          reject(new Error('Stream closed without receiving final response'))
        }
      },
      onerror(err) {
        console.error('Error in searchMultiAgents stream:', err)
        reject(err)
      },
    })
  })
}

export const updateUserFeedback = async (
  baseUrl: string,
  traceId: string,
  isHelpful: boolean,
  comment?: string,
): Promise<void> => {
  try {
    const url = `${baseUrl}${API_PATHS.updateUserFeedback}`

    const payload = {
      traceId,
      isHelpful,
      comment,
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error calling updateUserFeedback API:', error)
    throw error
  }
}
